import { useCallback, useMemo, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import type { EpiClarifyingQuestion, EpiMessage } from '../types/epi-chat-types';
import { getPendingClarifyingQuestion } from '../utils/pendingClarifyingQuestion';

export interface UsePinnedClarifyingQuestionResult {
  /** The clarifying question to render pinned above the composer, or null
   *  (flag off / nothing pending / dismissed). */
  pinnedQuestion: EpiClarifyingQuestion | null;
  /** Hide the current pinned question without answering it. A later
   *  question (different id) pins again. */
  dismissPinnedQuestion: () => void;
}

/**
 * Behavior hook for the pinned clarifying-question panel: under
 * ``research_assistant_pinned_clarify``, surfaces the unanswered question on
 * the transcript's last message so the layouts can render it attached to the
 * composer. Dismissal is per-question (keyed by ``questionId``) and purely
 * local UI state — the in-stream transcript and the answer path are
 * untouched. TODO: cleanup with research_assistant_pinned_clarify ff
 */
export const usePinnedClarifyingQuestion = (
  messages: ReadonlyArray<EpiMessage>
): UsePinnedClarifyingQuestionResult => {
  const enabled = Boolean(useFeatureFlagSplit('research_assistant_pinned_clarify'));
  const [dismissedQuestionId, setDismissedQuestionId] = useState<string | null>(null);
  const pending = useMemo(
    () => (enabled ? getPendingClarifyingQuestion(messages) : null),
    [enabled, messages]
  );
  const pendingQuestionId = pending?.questionId ?? null;
  const dismissPinnedQuestion = useCallback(() => {
    setDismissedQuestionId(pendingQuestionId);
  }, [pendingQuestionId]);
  return {
    pinnedQuestion: pending && pending.questionId !== dismissedQuestionId ? pending : null,
    dismissPinnedQuestion,
  };
};
